// Which manifest a viewer should stream: the project's LIVE version.
//
// Every viewer used to open `uploads/<project>/manifest.json.gz`, the
// project-level copy that a promote rewrites. That copy is only as right
// as the last thing that wrote it: on 2026-10-31 a staging export wrote
// its own model over prod SpaceX BA03's (the buckets are shared), and
// every prod viewer streamed staging's 44-document model while prod's
// live version - and its search index - were the 17-document one. Find
// in Sheets & Model then lit elements the canvas did not have.
//
// A live version's own folder (`<project>/versions/<v>/`) is written once
// and never rewritten, so a viewer that opens it sees exactly the model
// the version row - and everything indexed from it - describes. Projects
// without a version row, or whose live row points at the legacy path
// (direct-to-models publishes), keep the project-level URL.
import { useEffect, useState } from "react";
import { url as apiUrl, tokenRef } from "api";

const VERSIONED = /^(?:uploads\/)?([0-9a-f]{24})\/versions\/[^/?#]+\/manifest\.json\.gz$/i;

export function legacyManifestUrl(base, projectId) {
  return `${base}/uploads/${projectId}/manifest.json.gz`;
}

// The URL to stream for `current` (the GET /model-versions/.../current
// row), or the project-level one when the row does not name a versioned
// manifest of THIS project.
export function manifestUrlFor(base, projectId, current) {
  const key = String((current && current.manifestKey) || "").replace(/^\/+/, "");
  const m = key.match(VERSIONED);
  if (!m || m[1].toLowerCase() !== String(projectId).toLowerCase()) return legacyManifestUrl(base, projectId);
  return `${base}/uploads/${key.replace(/^uploads\//i, "")}`;
}

// Never throws and never waits long: a failed or slow lookup opens the
// project-level copy, which is what every viewer did before.
export async function liveManifestUrl(projectId, { base = apiUrl, fetchImpl, timeoutMs = 8000 } = {}) {
  const doFetch = fetchImpl || (typeof fetch === "function" ? fetch : null);
  if (!projectId || !doFetch) return legacyManifestUrl(base, projectId);
  let timer = null;
  try {
    const token = (() => { try { return localStorage.getItem(tokenRef); } catch (e) { return null; } })();
    const req = doFetch(`${base}/model-versions/project/${encodeURIComponent(projectId)}/current`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const late = new Promise((resolve) => { timer = setTimeout(() => resolve(null), timeoutMs); });
    const r = await Promise.race([req, late]);
    if (!r || !r.ok) return legacyManifestUrl(base, projectId);
    const out = await r.json().catch(() => ({}));
    return manifestUrlFor(base, projectId, out && out.current);
  } catch (e) {
    return legacyManifestUrl(base, projectId);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// For a viewer built synchronously inside an effect: null until the
// lookup settles (it always settles - see liveManifestUrl), then the URL.
export function useLiveManifestUrl(projectId) {
  const [found, setFound] = useState({ projectId: null, manifestUrl: null });
  useEffect(() => {
    let alive = true;
    if (!projectId) return undefined;
    liveManifestUrl(projectId).then((manifestUrl) => { if (alive) setFound({ projectId, manifestUrl }); });
    return () => { alive = false; };
  }, [projectId]);
  return found.projectId === projectId ? found.manifestUrl : null;
}
